import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

import { topazSignatureProvider } from "./providers/topaz.provider";

/** Bump the version suffix to make every workstation re-earn its pad tab. */
export const SIGNATURE_PAD_WORKSTATION_STORAGE_KEY =
  "suno_signature_pad_workstation_v1";

/**
 * How long a workstation stays "a pad workstation" after its last successful
 * probe. Long enough that a pad unplugged over a holiday still shows the tab
 * (with the self-serve not-found state, which is where it gets fixed), short
 * enough that a machine repurposed to a different room stops advertising a pad
 * it no longer has.
 */
export const SIGNATURE_PAD_MEMORY_TTL_MS = 90 * 24 * 60 * 60 * 1000;

export type TopazProbeState = "idle" | "probing" | "available" | "unavailable";

interface StoredPadWorkstation {
  /** Epoch ms of the last successful probe. */
  lastSeenAt: number;
  version?: string | null;
  model?: string | null;
}

function isStoredPadWorkstation(data: unknown): data is StoredPadWorkstation {
  return typeof (data as StoredPadWorkstation)?.lastSeenAt === "number";
}

export interface TopazPadAvailability {
  state: TopazProbeState;
  /** SigWeb version from the most recent successful probe, if any. */
  version: string | null;
  /** Pad model from the most recent successful probe, if any. */
  model: string | null;
  /** True while an unexpired successful probe is remembered for this browser. */
  isRemembered: boolean;
  /** Re-probe now. Safe to call while a probe is in flight. */
  recheck: () => void;
  /**
   * Record a successful probe made elsewhere — the desk's own detection, or
   * its "Check again". Without this, a workstation that reached the pad the
   * one way the probe cannot predict (staff clicking "Using a signature pad?"
   * before the driver was installed) would have to be told to click it again
   * every single time.
   */
  remember: (info: { version?: string | null; model?: string | null }) => void;
}

/**
 * Decides whether this workstation gets the "Signature pad" tab.
 *
 * Two inputs, because either alone gets a real case wrong:
 *
 * - A live probe, so a practice that plugs in a pad sees the tab without being
 *   told to click anything. This is the whole rollout: the five practices on
 *   the flag own pads already.
 * - A remembered success, so the tab survives SigWeb being stopped, its
 *   certificate expiring (they expire yearly — see the folder's CLAUDE.md) or
 *   the pad being unplugged. Those are exactly the moments staff need the
 *   not-found state's setup links, and hiding the tab would hide the fix.
 *
 * Memory is per browser rather than per clinic on purpose, for the same reason
 * `useSignatureMethod` is: a pad is cabled to one machine, so a practice-level
 * setting would promise a pad to every roaming laptop in the building.
 */
export function useTopazPadAvailability(
  enabled: boolean
): TopazPadAvailability {
  const storage = useMemo(
    () => setupLocalStorageAPI(SIGNATURE_PAD_WORKSTATION_STORAGE_KEY),
    []
  );

  const readRemembered = useCallback((): StoredPadWorkstation | null => {
    const stored = storage.getData(null, isStoredPadWorkstation);
    if (!isStoredPadWorkstation(stored)) return null;
    // An expired record is dropped rather than ignored, so a machine that
    // genuinely lost its pad stops carrying the row forever.
    if (Date.now() - stored.lastSeenAt > SIGNATURE_PAD_MEMORY_TTL_MS) {
      storage.clear();
      return null;
    }
    return stored;
  }, [storage]);

  const [remembered, setRemembered] = useState<StoredPadWorkstation | null>(
    () => (enabled ? readRemembered() : null)
  );
  const [state, setState] = useState<TopazProbeState>("idle");
  const [live, setLive] = useState<{
    version: string | null;
    model: string | null;
  } | null>(null);
  const [attempt, setAttempt] = useState(0);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setState("idle");
      return undefined;
    }

    let cancelled = false;
    // Re-read on every enable, not just on mount. Almost every call site keeps
    // a closed `SignaturePicker` mounted and flips `open`, so a mount-time read
    // happens while the hook is still disabled and would leave a genuine pad
    // workstation permanently unremembered — the tab would then vanish exactly
    // when SigWeb stops answering, which is when it is needed.
    setRemembered(readRemembered());
    setState("probing");

    (async () => {
      // `isAvailable` is contractually non-throwing, but this runs on the
      // signing path — a provider regression must degrade to "no tab", never
      // to an unhandled rejection in the dialog.
      const status = await topazSignatureProvider
        .isAvailable()
        .catch(() => ({ available: false }) as const);
      if (cancelled || !isMountedRef.current) return;

      if (!status.available) {
        setState("unavailable");
        setLive(null);
        return;
      }

      const version = ("version" in status ? status.version : null) ?? null;
      const model = ("model" in status ? status.model : null) ?? null;
      const record: StoredPadWorkstation = {
        lastSeenAt: Date.now(),
        version,
        model
      };
      storage.updateData(record);
      setRemembered(record);
      setLive({ version, model });
      setState("available");
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, storage, attempt, readRemembered]);

  const recheck = useCallback(() => setAttempt((value) => value + 1), []);

  const remember = useCallback(
    (info: { version?: string | null; model?: string | null }) => {
      const record: StoredPadWorkstation = {
        lastSeenAt: Date.now(),
        version: info.version ?? null,
        model: info.model ?? null
      };
      storage.updateData(record);
      setRemembered(record);
    },
    [storage]
  );

  return {
    state,
    version: live?.version ?? remembered?.version ?? null,
    model: live?.model ?? remembered?.model ?? null,
    isRemembered: enabled && remembered !== null,
    recheck,
    remember
  };
}
